import {createSlice, createAsyncThunk} from '@reduxjs/toolkit';
import {fetchOrganisations} from '../api/organisationApi';
import {API_BASE_URL, DOWNLOAD_SERVER} from '../app/config';
import {getOrganisationPreview} from '../api/taskApi';

const buildOrganisationLogoUrl = (logo, organisationId = null) => {
    if (!logo || typeof logo !== 'string') {
        return null;
    }

    // If it's already a full URL, return it as is
    if (/^https?:\/\//i.test(logo)) {
        return logo;
    }

    // Use new DOWNLOAD_SERVER if available and organisationId is provided
    if (DOWNLOAD_SERVER && organisationId) {
        try {
            return getOrganisationPreview(organisationId, logo);
        } catch (error) {
            // Fall through to old method
        }
    }

    // Fallback to old method using API_BASE_URL
    const baseUrl = (API_BASE_URL || '').replace(/\/$/, '');
    if (!baseUrl) {
        return logo;
    }

    return `${baseUrl}/storage/organisations/${logo}`;
};

export const fetchOrganisationsAction = createAsyncThunk(
    'organisation/fetchOrganisations',
    async (userId, {rejectWithValue}) => {
        try {
            return await fetchOrganisations(userId);
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

const organisationSlice = createSlice({
    name: 'organisation',
    initialState: {
        organisations: [],
        selectedOrganisation: null,
        selectedOrganisations: [],
        loading: false,
        error: null,
    },
    reducers: {
        setSelectedOrganisation: (state, action) => {
            state.selectedOrganisation = action.payload;
            const selectedId = action.payload?.id;
            if (selectedId && !state.selectedOrganisations.includes(selectedId)) {
                state.selectedOrganisations.push(selectedId);
            }
        },
        toggleOrganisationSelection: (state, action) => {
            const organisationId = action.payload;
            if (!Array.isArray(state.selectedOrganisations)) {
                state.selectedOrganisations = [];
            }
            if (state.selectedOrganisations.includes(organisationId)) {
                state.selectedOrganisations = state.selectedOrganisations.filter((id) => id !== organisationId);
            } else {
                state.selectedOrganisations.push(organisationId);
            }
        },
        setSelectedOrganisations: (state, action) => {
            state.selectedOrganisations = Array.isArray(action.payload) ? action.payload : [];
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchOrganisationsAction.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchOrganisationsAction.fulfilled, (state, action) => {
                state.loading = false;

                const payload = action.payload;
                // Extract error message only (serializable), not the whole error object
                let payloadError = null;
                if (payload && typeof payload === 'object' && 'error' in payload) {
                    const errorValue = payload.error;
                    // If error is an object (like AxiosError), extract message
                    if (errorValue && typeof errorValue === 'object') {
                        payloadError = errorValue?.message 
                            || errorValue?.response?.data?.message 
                            || 'Eroare necunoscută';
                    } else if (typeof errorValue === 'string') {
                        payloadError = errorValue;
                    }
                }

                state.error = payloadError;
                const rawOrganisations = Array.isArray(payload)
                    ? payload
                    : Array.isArray(payload?.data)
                        ? payload.data
                        : [];

                const organisationsData = rawOrganisations.map((organisation) => {
                    const logoSource = organisation?.organisation_logo || organisation?.logo;
                    const organisationId = organisation?.id || organisation?.organisation_id;
                    return {
                        ...organisation,
                        organisation_logo: buildOrganisationLogoUrl(logoSource, organisationId),
                    };
                });

                state.organisations = organisationsData;

                // Log organisations structure to see what fields are available
                if (organisationsData.length > 0) {
                    console.log('[Organisations] First organisation structure:', organisationsData[0]);
                }

                // Find default organisation from API - check multiple possible field names
                const defaultOrganisation = organisationsData.find((org) => 
                    org.default === true || 
                    org.is_default === true || 
                    org.default_organisation === true ||
                    org.main === true ||
                    org.is_main === true ||
                    org.main_organisation === true ||
                    org.default_org === true ||
                    (typeof org.default === 'number' && org.default === 1) ||
                    (typeof org.is_default === 'number' && org.is_default === 1) ||
                    (typeof org.main === 'number' && org.main === 1)
                );

                console.log('[Organisations] Default organisation found:', defaultOrganisation ? {
                    id: defaultOrganisation.id,
                    name: defaultOrganisation.organisation_name,
                    default: defaultOrganisation.default,
                    is_default: defaultOrganisation.is_default,
                    main: defaultOrganisation.main
                } : null);

                // Preserve existing selected organisations from dropdown (don't reset on refresh)
                // This ensures selections remain even after automatic refresh every 5 minutes
                const existingSelectedIds = Array.isArray(state.selectedOrganisations) && state.selectedOrganisations.length > 0
                    ? [...state.selectedOrganisations]
                    : [];

                // Validate existing selected IDs against new organisations data
                // Only remove IDs that no longer exist in the API response
                const validSelectedIds = existingSelectedIds.filter((id) =>
                    organisationsData.some((org) => org.id === id)
                );

                // Update selectedOrganisations with valid IDs (preserve dropdown selections)
                // Only update if we have existing selections to preserve, otherwise keep current state
                if (existingSelectedIds.length > 0) {
                    state.selectedOrganisations = validSelectedIds;
                } else if (state.selectedOrganisations.length > 0) {
                    // If existingSelectedIds was empty but state has selections, validate and keep them
                    const currentValidIds = state.selectedOrganisations.filter((id) =>
                        organisationsData.some((org) => org.id === id)
                    );
                    state.selectedOrganisations = currentValidIds;
                }

                // Always update selectedOrganisation to be the default/main organisation from API
                // If no default in API, use first organisation from list
                // This ensures the main organisation is always up-to-date after refresh
                if (defaultOrganisation) {
                    state.selectedOrganisation = defaultOrganisation;
                    // Ensure default organisation is in selectedOrganisations if not already there
                    if (!state.selectedOrganisations.includes(defaultOrganisation.id)) {
                        state.selectedOrganisations.push(defaultOrganisation.id);
                    }
                } else if (organisationsData.length > 0) {
                    // No default in API - use first organisation from list
                    state.selectedOrganisation = organisationsData[0];
                    // Ensure first organisation is in selectedOrganisations if not already there
                    if (!state.selectedOrganisations.includes(organisationsData[0].id)) {
                        state.selectedOrganisations.push(organisationsData[0].id);
                    }
                } else {
                    // No organisations at all
                    state.selectedOrganisation = null;
                }
            })
            .addCase(fetchOrganisationsAction.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            });
    }
});

export const {
    setSelectedOrganisation,
    toggleOrganisationSelection,
    setSelectedOrganisations
} = organisationSlice.actions;
export default organisationSlice.reducer;
